/**
 * Countdown Timer 倒计时逻辑
 * 改为基于时间戳计算，避免标签页被节流后走慢
 */

class CountdownTimer {
    constructor() {
        this.totalMs = 0; // 总毫秒
        this.remainingMs = 0; // 剩余毫秒
        this.endTimestamp = null; // 预期结束时间戳（performance.now）
        this.rafId = null;
        this.isRunning = false;
        this.isNegative = false; // 是否已经进入负数（超时）状态
        this.lastDisplay = null; // 上次渲染的字符串，避免重复更新
        this.callbacks = {
            onTick: null,
            onComplete: null,
            onStart: null,
            onPause: null,
            onReset: null
        };

        this._tickRaf = this._tickRaf.bind(this);
    }

    /**
     * 设置倒计时时间
     * @param {number} hours - 小时
     * @param {number} minutes - 分钟
     * @param {number} seconds - 秒
     */
    setTime(hours, minutes, seconds) {
        this.totalMs = (hours * 3600 + minutes * 60 + seconds) * 1000;
        this.remainingMs = this.totalMs;
        this.isNegative = false;
        this.endTimestamp = null;
        this.lastDisplay = null;
        this._triggerCallback('onTick', this.getFormattedTime());

        // 如果在运行，重置结束时间，保证新的时间生效
        if (this.isRunning) {
            this.start();
        }
    }

    /**
     * 获取格式化的时间字符串 HH:MM:SS
     */
    getFormattedTime() {
        const absMs = Math.max(0, Math.abs(this.remainingMs));
        const totalSeconds = Math.floor(absMs / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        const formatted = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

        return this.isNegative ? `-${formatted}` : formatted;
    }

    /**
     * 开始倒计时
     */
    start() {
        if (this.isRunning) return;

        // 重新计算目标结束时间
        this.endTimestamp = performance.now() + this.remainingMs;
        this.isRunning = true;
        this._triggerCallback('onStart');
        this._tickRaf();
    }

    /**
     * 暂停倒计时
     */
    pause() {
        if (!this.isRunning) return;

        this._syncRemaining();
        this.isRunning = false;
        this.endTimestamp = null;
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        this._triggerCallback('onPause');
    }

    /**
     * 重置倒计时
     */
    reset() {
        this.pause();
        this.remainingMs = this.totalMs;
        this.isNegative = false;
        this.lastDisplay = null;
        this._triggerCallback('onReset');
        this._triggerCallback('onTick', this.getFormattedTime());
    }

    /**
     * 获取当前状态
     */
    getState() {
        return {
            totalSeconds: Math.floor(this.totalMs / 1000),
            remainingSeconds: Math.floor(this.remainingMs / 1000),
            isRunning: this.isRunning,
            isNegative: this.isNegative,
            formattedTime: this.getFormattedTime()
        };
    }

    /**
     * 注册回调函数
     * @param {string} event - 事件名称 (onTick, onComplete, onStart, onPause, onReset)
     * @param {Function} callback - 回调函数
     */
    on(event, callback) {
        if (this.callbacks.hasOwnProperty(event)) {
            this.callbacks[event] = callback;
        }
    }

    /**
     * 触发回调函数
     */
    _triggerCallback(event, data) {
        if (this.callbacks[event] && typeof this.callbacks[event] === 'function') {
            this.callbacks[event](data);
        }
    }

    /**
     * 同步剩余时间
     */
    _syncRemaining() {
        if (this.endTimestamp) {
            this.remainingMs = this.endTimestamp - performance.now();
            this.isNegative = this.remainingMs < 0;
        }
    }

    /**
     * rAF 驱动的更新循环
     */
    _tickRaf() {
        if (!this.isRunning) return;

        const now = performance.now();
        this.remainingMs = this.endTimestamp - now;
        const wasNegative = this.isNegative;
        this.isNegative = this.remainingMs < 0;

        const display = this.getFormattedTime();
        if (display !== this.lastDisplay) {
            this.lastDisplay = display;
            this._triggerCallback('onTick', display);
        }

        if (this.isNegative && !wasNegative) {
            this._triggerCallback('onComplete');
        }

        this.rafId = requestAnimationFrame(this._tickRaf);
    }

    /**
     * 销毁定时器
     */
    destroy() {
        this.pause();
        this.callbacks = {};
    }
}

// 导出
export default CountdownTimer;
